import React, { useState, useEffect } from "react";
import { AiOutlineClose } from "react-icons/ai";
import apiEndpoints from "../../apiconfig";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import axios from "axios";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";
import { useSnackbar } from "../../DynamicComponents/SnackbarContext";


const AddContactModal = ({ onClose, refreshContacts, contactToEdit }) => {
  const [groupOptions, setGroupOptions] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedGroup, setSelectedGroup] = useState("");
  const [isFocused, setIsFocused] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [errors, setErrors] = useState({});
  const [mode, setMode] = useState("single");
  const [allContacts, setAllContacts] = useState([]);
  const [selectedContacts, setSelectedContacts] = useState([]);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const { showSnackbar } = useSnackbar();
  const [confirmDeleteGroup, setConfirmDeleteGroup] = useState(null);



  const [formData, setFormData] = useState({
    country_code: "+91",
    mobile_number: "",
    contact_name: "",
    tags: "",
  });

  // Get token from localStorage
  const getToken = () => {
    return localStorage.getItem('token');
  };

  // Get auth headers
  const getAuthHeaders = () => {
    const token = getToken();
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    };
  };

  const fetchGroups = async () => {
    try {
      const token = getToken();
      const res = await axios.get(apiEndpoints.getGroups, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const data = res.data;
      if (data.status && Array.isArray(data.data)) {
        setGroupOptions(data.data.map((g) => g.groupname));
      } else {
        setGroupOptions([]);
      }
    } catch (err) {
      console.error("Error fetching groups:", err);
      setGroupOptions([]);
      if (err.response?.status === 401) {
        showSnackbar("Session expired. Please login again.", "error");
      }
    }
  };

  const fetchAllContacts = async () => {
    try {
      const token = getToken();
      const res = await axios.get(apiEndpoints.addContact, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const data = res.data;
      if (Array.isArray(data)) {
        setAllContacts(data);
      } else if (Array.isArray(data.data)) {
        setAllContacts(data.data);
      } else {
        setAllContacts([]);
      }
    } catch (err) {
      console.error("Error fetching all contacts:", err);
      setAllContacts([]);
      if (err.response?.status === 401) {
        showSnackbar("Session expired. Please login again.", "error");
      }
    }
  };

  useEffect(() => {
    fetchGroups();
    fetchAllContacts();
  }, []);

  useEffect(() => {
    if (contactToEdit) {
      setFormData({
        country_code: contactToEdit.country_code || "+91",
        mobile_number: contactToEdit.mobile_number || "",
        contact_name: contactToEdit.contact_name || "",
        tags: contactToEdit.tags || "",
      });
      setSelectedGroup(contactToEdit.contact_group || "");
      setSearchTerm(contactToEdit.contact_group || "");
    }
  }, [contactToEdit]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    // If mobile_number, allow digits only and enforce max 10 chars
    if (name === "mobile_number") {
      const cleaned = value.replace(/\D/g, "").slice(0, 10);
      setFormData((prev) => ({ ...prev, [name]: cleaned }));
      setErrors((prev) => ({ ...prev, [name]: "" }));
      return;
    }

    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" })); // clear field error
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.contact_name.trim())
      newErrors.contact_name = "Contact name is required";
    if (!formData.mobile_number.trim()) {
      newErrors.mobile_number = "Mobile number is required";
    } else if (!/^[0-9]{10}$/.test(formData.mobile_number.trim())) {
      newErrors.mobile_number = "Mobile number must be exactly 10 digits";
    }
    return newErrors;
  };

  const handleAddGroup = async () => {
    if (!searchTerm.trim()) {
      showSnackbar("Enter group name", "error");
      return;
    }
    
    try {
      const token = getToken();
      const res = await axios.post(apiEndpoints.addContact, {
        action: "add_group",
        contact_group: searchTerm.trim(),
      }, {
        headers: getAuthHeaders()
      });
      
      const result = res.data;
      if (result.status) {
        showSnackbar(result.message, "success");
        fetchGroups();
        setSelectedGroup(searchTerm.trim());
      } else {
        showSnackbar(result.message, "error");
      }
    } catch (err) {
      console.error("Error adding group:", err);
      if (err.response?.status === 401) {
        showSnackbar("Session expired. Please login again.", "error");
      } else {
        showSnackbar("Failed to add group", "error");
      }
    }
  };

 const handleSubmit = async (e) => {
  e.preventDefault();
  setIsSubmitting(true);
  setError(null);

  // Validate form fields
  const fieldErrors = validateForm();
  if (Object.keys(fieldErrors).length > 0) {
    setErrors(fieldErrors);
    setIsSubmitting(false);
    return;
  }

  try {
    const token = getToken();
    if (!token) {
      showSnackbar("Authentication token not found. Please login again.", "error");
      throw new Error("Authentication token not found.");
    }

    // ==============================
    // 📌 GROUP MODE
    // ==============================
    if (mode === "group") {
      if (!searchTerm.trim()) {
        showSnackbar("Please enter a group name", "error");
        setIsSubmitting(false);
        return;
      }
      if (selectedContacts.length === 0) {
        showSnackbar("Select at least one contact for the group", "error");
        setIsSubmitting(false);
        return;
      }

      const res = await axios.post(
        apiEndpoints.addContact,
        {
          action: "create_group",
          group_name: searchTerm.trim(),
          contacts: selectedContacts,
        },
        {
          headers: getAuthHeaders(),
        }
      );

      const result = res.data;
      if (!result.status) {
        showSnackbar(result.message || "Failed to create group", "error");
        throw new Error(result.message || "Failed to create group");
      }

      // Group created successfully
      showSnackbar("Group created successfully!", "success");
    }

    // ==============================
    // 📌 SINGLE CONTACT MODE
    // ==============================
    else {
      const groupToSend =
        selectedGroup?.trim() || searchTerm?.trim() || "Ungrouped";

      const payload = {
        contact_group: groupToSend,
        country_code: formData.country_code,
        mobile_number: formData.mobile_number,
        contact_name: formData.contact_name,
        tags: formData.tags || "",
        action: contactToEdit ? "edit" : "add",
        ...(contactToEdit ? { id: contactToEdit.id } : {}),
      };

      const response = await axios.post(apiEndpoints.addContact, payload, {
        headers: getAuthHeaders(),
      });

      const result = response.data;
      if (!result.status) {
        showSnackbar(result.message || "Operation failed", "error");
        throw new Error(result.message || "Operation failed");
      }

      // NEW CONTACT
      if (!contactToEdit) {
        showSnackbar("New contact created successfully!", "success");
      } 
      // EDIT CONTACT
      else {
        showSnackbar("Contact updated successfully!", "success");
      }
    }

    if (refreshContacts) refreshContacts();
    onClose();

  } catch (err) {
    console.error("Submit error:", err);
    showSnackbar(err.message || "Operation failed", "error");
  } finally {
    setIsSubmitting(false);
  }
};


  const filteredOptions = Array.isArray(groupOptions)
    ? groupOptions.filter(
        (g) =>
          typeof g === "string" &&
          g.toLowerCase().includes((searchTerm || "").toLowerCase())
      )
    : [];

  // Helper to allow only navigation/control keys + digits
  const handleMobileKeyDown = (e) => {
    const allowedKeys = [
      "Backspace",
      "Delete",
      "ArrowLeft",
      "ArrowRight",
      "Tab",
      "Home",
      "End",
    ];
    if (allowedKeys.includes(e.key)) return;
    // allow Ctrl/Cmd + C/V/X/A
    if (
      (e.ctrlKey || e.metaKey) &&
      ["a", "c", "v", "x"].includes(e.key.toLowerCase())
    )
      return;
    // only digits
    if (!/^[0-9]$/.test(e.key)) {
      e.preventDefault();
    }
  };

  return (
    <>
      <ToastContainer position="top-right" autoClose={3000} theme="colored" />
      <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 px-4 font-['Montserrat']">
        <div
          className="bg-white w-full max-w-lg rounded-xl shadow-xl overflow-hidden animate-fadeIn flex flex-col"
          style={{
            maxHeight: "90vh",
            height: "auto",
          }}
        >
          {/* Header */}
          <div className="flex justify-between items-center px-6 py-4 bg-[#25D366] text-white">
            <h2 className="text-lg font-semibold">
              {contactToEdit ? "Edit Contact" : "Add New Contact"}
            </h2>
            <button
              onClick={onClose}
              className="p-1 hover:bg-white/20 rounded-full transition"
            >
              <AiOutlineClose size={20} />
            </button>
          </div>

          {/* Form Body + Footer inside */}
          <form
            onSubmit={handleSubmit}
            className="flex flex-col flex-1 overflow-hidden"
          >
            <div
              className="flex-1 overflow-y-auto p-6 space-y-5 custom-scrollbar"
              style={{
                maxHeight: "calc(90vh - 120px)",
              }}
            >
              {/* Toggle Buttons */}
              <div className="flex gap-3 mb-3">
                <button
                  type="button"
                  onClick={() => setMode("single")}
                  className={`flex-1 py-2 rounded-md font-medium transition ${
                    mode === "single"
                      ? "bg-[#25D366] text-white shadow"
                      : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  }`}
                >
                  Single Contact
                </button>
                <button
                  type="button"
                  onClick={() => setMode("group")}
                  className={`flex-1 py-2 rounded-md font-medium transition ${
                    mode === "group"
                      ? "bg-[#25D366] text-white shadow"
                      : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  }`}
                >
                  Create Group
                </button>
              </div>

              {error && (
                <div className="p-2 bg-red-100 border border-red-300 text-red-700 rounded text-sm">
                  {error}
                </div>
              )}

              {/* SINGLE MODE */}
              {mode === "single" ? (
                <>
                  {/* Group (Optional) */}
                  <div className="relative">
                    <label className="text-sm font-medium text-gray-700">
                      Group (Optional)
                    </label>
                    <div className="mt-1 flex flex-col sm:flex-row sm:items-center gap-2">
                      <input
                        type="text"
                        placeholder="Select or enter group (optional)"
                        value={searchTerm}
                        onChange={(e) => {
                          setSearchTerm(e.target.value);
                          setSelectedGroup(e.target.value);
                        }}
                        onFocus={() => setIsFocused(true)}
                        className="flex-1 border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-[#25D366] outline-none w-full"
                      />
                      <button
                        type="button"
                        onClick={handleAddGroup}
                        className="bg-[#25D366] text-white px-4 py-2 rounded-md text-sm hover:bg-[#1cb85c] w-full sm:w-auto"
                      >
                        Add
                      </button>
                    </div>

                    {isFocused && filteredOptions.length > 0 && (
                      <ul className="absolute bg-white border border-gray-200 rounded-md mt-1 w-full max-h-36 overflow-y-auto shadow z-50">
                        {filteredOptions.map((g, i) => (
                          <li
                            key={i}
                            onMouseDown={(e) => {
                              e.preventDefault(); // 🔥 prevents blur
                              setSearchTerm(g);
                              setSelectedGroup(g);
                              setIsFocused(false);
                            }}
                            className="px-3 py-2 hover:bg-gray-100 cursor-pointer"
                          >
                            {g}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>

                  {/* Country Code + Number */}
                  <div className="flex flex-col sm:flex-row sm:gap-3 gap-2">
                    <div className="sm:w-1/3 w-full">
                      <label className="text-sm font-medium text-gray-700">
                        Country Code
                      </label>
                      <select
                        name="country_code"
                        value={formData.country_code}
                        onChange={handleInputChange}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-[#25D366] outline-none"
                      >
                        <option value="+91">+91 (India)</option>
                        <option value="+1">+1 (USA)</option>
                        <option value="+44">+44 (UK)</option>
                        <option value="+65">+65 (Singapore)</option>
                        <option value="+971">+971 (UAE)</option>
                      </select>
                    </div>

                    <div className="sm:flex-1 w-full">
                      <label className="text-sm font-medium text-gray-700">
                        Mobile Number
                      </label>
                      <input
                        type="tel"
                        name="mobile_number"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        maxLength={10}
                        value={formData.mobile_number}
                        onChange={handleInputChange}
                        onKeyDown={handleMobileKeyDown}
                        placeholder="Enter 10-digit number"
                        className={`w-full border rounded-md px-3 py-2 focus:ring-2 outline-none ${
                          errors.mobile_number
                            ? "border-red-400 focus:ring-red-300"
                            : "border-gray-300 focus:ring-[#25D366]"
                        }`}
                      />
                      {errors.mobile_number ? (
                        <p className="text-red-600 text-xs mt-1">
                          {errors.mobile_number}
                        </p>
                      ) : (
                        <p className="text-gray-400 text-xs mt-1">
                          Must be a 10-digit valid mobile number.
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Contact Name */}
                  <div>
                    <label className="text-sm font-medium text-gray-700">
                      Contact Name
                    </label>
                    <input
                      type="text"
                      name="contact_name"
                      value={formData.contact_name}
                      onChange={handleInputChange}
                      placeholder="Enter name"
                      className={`w-full border rounded-md px-3 py-2 focus:ring-2 outline-none ${
                        errors.contact_name
                          ? "border-red-400 focus:ring-red-300"
                          : "border-gray-300 focus:ring-[#25D366]"
                      }`}
                    />
                    {errors.contact_name && (
                      <p className="text-red-600 text-xs mt-1">
                        {errors.contact_name}
                      </p>
                    )}
                  </div>

                  {/* Tags */}
                  <div>
                    <label className="text-sm font-medium text-gray-700">
                      Tags
                    </label>
                    <input
                      type="text"
                      name="tags"
                      value={formData.tags}
                      onChange={handleInputChange}
                      placeholder="Comma separated tags"
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-[#25D366] outline-none"
                    />
                  </div>
                </>
              ) : (
                <>
                  {/* GROUP MODE */}
                  <div>
                    <label className="text-sm font-medium text-gray-700">
                      Group Name
                    </label>
                    <input
                      type="text"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      placeholder="Enter new group name"
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-[#25D366] outline-none"
                    />
                  </div>

                  <div className="border rounded-md p-3 max-h-60 overflow-y-auto">
                    {allContacts.length === 0 ? (
                      <p className="text-sm text-gray-500">
                        No contacts available.
                      </p>
                    ) : (
                      allContacts.map((c) => (
                        <label
                          key={c.id}
                          className="flex items-center gap-2 py-1 cursor-pointer"
                        >
                          <input
                            type="checkbox"
                            checked={selectedContacts.includes(c.id)}
                            onChange={() =>
                              setSelectedContacts((prev) =>
                                prev.includes(c.id)
                                  ? prev.filter((id) => id !== c.id)
                                  : [...prev, c.id]
                              )
                            }
                          />
                          <span className="text-gray-700 text-sm">
                            {c.contact_name} ({c.mobile_number})
                          </span>
                        </label>
                      ))
                    )}
                  </div>
                </>
              )}
            </div>

            {/* Footer Buttons (inside form) */}
            <div className="flex flex-col sm:flex-row sm:justify-end gap-2 sm:gap-3 p-4 border-t border-gray-300 bg-white sticky bottom-0">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-100 text-sm font-medium"
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="bg-[#25D366] hover:bg-[#1cb85c] text-white px-6 py-2 rounded-md font-medium text-sm disabled:opacity-50"
              >
                {isSubmitting
                  ? contactToEdit
                    ? "Updating..."
                    : "Saving..."
                  : contactToEdit
                  ? "Update Contact"
                  : "Save Contact"}
              </button>
            </div>
          </form>
        </div>
      </div>
      {/* ⬇️ ADD SNACKBAR HERE  */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <MuiAlert
          onClose={() => setSnackbarOpen(false)}
          severity="success"
          variant="filled"
          elevation={6}
        >
          New contact created successfully!
        </MuiAlert>
      </Snackbar>
    </>
  );
};

export default AddContactModal;